// src/services/floodRiskService.js

// --- UPDATE: shared severity constants, configuration defaults and internal geo constants
/**
 * Severity levels emitted by the flood risk model, ordered from least to most severe.
 * @readonly
 * @enum {string}
 */
export const FLOOD_SEVERITY = Object.freeze({
  NONE: 'NONE',
  WARNING: 'WARNING',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
});

const SEVERITY_RANK = Object.freeze({ NONE: 0, WARNING: 1, HIGH: 2, CRITICAL: 3 });

/**
 * Numeric rank of a severity label (unknown labels rank as NONE).
 * @param {string} severity
 * @returns {number}
 */
export const getSeverityRank = (severity) => SEVERITY_RANK[severity] ?? 0;

/**
 * Default model configuration. Every key can be overridden through the `options` argument.
 * Defaults reproduce the original behaviour exactly (HIGH/CRITICAL/FLOOD/OVERFLOW or >= 90
 * marks a well as critical, 400 m road buffer, stale readings still count).
 */
export const FLOOD_RISK_DEFAULTS = Object.freeze({
  hazardRadiusMeters: 400,
  pipeHazardRadiusMeters: 250,
  warningThreshold: 75,
  highThreshold: 90,
  criticalThreshold: 100,
  warningStatuses: Object.freeze(['ELEVATED', 'WARNING', 'RISING']),
  highStatuses: Object.freeze(['HIGH']),
  criticalStatuses: Object.freeze(['CRITICAL', 'FLOOD', 'OVERFLOW']),
  wellTypes: Object.freeze(['well']),
  roadTypes: Object.freeze(['road', 'street']),
  pipeTypes: Object.freeze(['pipe', 'pipeline', 'main', 'water_main']),
  /** Readings older than this are flagged `isStale`. null disables staleness checks. */
  maxReadingAgeMs: null,
  /** When true, stale readings are ignored entirely instead of only being flagged. */
  excludeStaleReadings: false,
  /** Caps the impacted-road list per well (the full count is still reported). */
  maxImpactedRoadsPerWell: 100,
  /** Reuse pre-computed asset geometry while the `assets` array reference is unchanged. */
  useCache: true,
  /** Clock override (ms epoch), useful for tests and replaying historical telemetry. */
  now: null,
});

const DEG_TO_RAD = Math.PI / 180;
const METERS_PER_DEG_LAT = 111320;
const GRID_CELL_DEG = 0.01; // ~1.1 km cells — comfortably larger than the default hazard radii
const MAX_GRID_CELLS_PER_ITEM = 400; // very long assets go to an "oversized" bucket instead
const EMPTY_ARRAY = Object.freeze([]);
// --- END UPDATE

/**
 * Computes great-circle distance between two geographic coordinates using the Haversine formula.
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} Distance in meters
 */
export const computeDistanceMeters = (lat1, lon1, lat2, lon2) => {
  if (
    lat1 === undefined || lat1 === null ||
    lon1 === undefined || lon1 === null ||
    lat2 === undefined || lat2 === null ||
    lon2 === undefined || lon2 === null
  ) {
    return Infinity;
  }

  // --- UPDATE: reject non-numeric inputs (previously yielded NaN, which silently failed every `<=` check)
  if (
    !Number.isFinite(Number(lat1)) ||
    !Number.isFinite(Number(lon1)) ||
    !Number.isFinite(Number(lat2)) ||
    !Number.isFinite(Number(lon2))
  ) {
    return Infinity;
  }
  // --- END UPDATE

  const R = 6371e3; // Earth radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// --- UPDATE: coordinate / parsing helpers
const toCoord = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const isValidLatLng = (lat, lng) =>
  lat !== null && lng !== null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

const normalizeType = (asset) => String(asset?.type ?? '').toLowerCase().trim();

const parseMaybeJson = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/** Accepts {lat,lng}, {latitude,longitude}, {lat,lon} or GeoJSON [lng, lat]. */
const toPoint = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    const lng = toCoord(raw[0]);
    const lat = toCoord(raw[1]);
    return isValidLatLng(lat, lng) ? { lat, lng } : null;
  }
  const lat = toCoord(raw.lat ?? raw.latitude);
  const lng = toCoord(raw.lng ?? raw.lon ?? raw.longitude);
  return isValidLatLng(lat, lng) ? { lat, lng } : null;
};

const toPointList = (rawList) =>
  Array.isArray(rawList) ? rawList.map(toPoint).filter(Boolean) : [];

/**
 * Parses timestamps given as Date, ISO string, epoch ms, epoch seconds or numeric strings.
 * @returns {number|null} epoch milliseconds
 */
const parseTimestamp = (ts) => {
  if (ts === null || ts === undefined || ts === '') return null;
  if (ts instanceof Date) return Number.isFinite(ts.getTime()) ? ts.getTime() : null;
  if (typeof ts === 'number') return Number.isFinite(ts) ? (ts < 1e12 ? ts * 1000 : ts) : null;
  const numeric = Number(ts);
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = Date.parse(ts);
  return Number.isFinite(parsed) ? parsed : null;
};

const getReading = (readings, deviceId) => {
  if (!readings || deviceId === undefined || deviceId === null) return undefined;
  if (readings instanceof Map) return readings.get(deviceId) ?? readings.get(String(deviceId));
  return readings[deviceId];
};
// --- END UPDATE

// --- UPDATE: geometry extraction — polylines (path_coordinates / GeoJSON LineString / MultiLineString) plus point
/**
 * Builds a reusable geometry record for an asset. Every polyline part is kept separate so
 * MultiLineStrings never get a phantom segment joining their pieces. The asset's own
 * lat/lng (when valid) is kept as an extra point part, so a road the original point-based
 * check flagged is always still flagged.
 */
const buildGeometry = (asset) => {
  const parts = [];

  const path = toPointList(parseMaybeJson(asset.path_coordinates));
  if (path.length > 0) parts.push(path);

  if (parts.length === 0) {
    const rawGeo = parseMaybeJson(asset.geometry ?? asset.geojson);
    const geo = rawGeo?.type === 'Feature' ? rawGeo.geometry : rawGeo;
    if (geo?.type === 'LineString') {
      const line = toPointList(geo.coordinates);
      if (line.length > 0) parts.push(line);
    } else if (geo?.type === 'MultiLineString' && Array.isArray(geo.coordinates)) {
      geo.coordinates.forEach((segment) => {
        const line = toPointList(segment);
        if (line.length > 0) parts.push(line);
      });
    } else if (geo?.type === 'Point') {
      const p = toPoint(geo.coordinates);
      if (p) parts.push([p]);
    }
  }

  const hasPolyline = parts.some((part) => part.length > 1);

  const lat = toCoord(asset.latitude);
  const lng = toCoord(asset.longitude);
  if (isValidLatLng(lat, lng)) parts.push([{ lat, lng }]);

  if (parts.length === 0) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  parts.forEach((part) =>
    part.forEach((p) => {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    })
  );

  return {
    asset,
    parts,
    bbox: { minLat, maxLat, minLng, maxLng },
    matchedBy: hasPolyline ? 'polyline' : 'point',
  };
};

/**
 * Nearest point on a geometry to (lat0, lng0). Candidates are compared in a local
 * equirectangular projection (sub-metre error at these radii); the winner's distance
 * is then computed exactly with Haversine.
 */
const nearestOnGeometry = (lat0, lng0, geom) => {
  const kx = METERS_PER_DEG_LAT * Math.cos(lat0 * DEG_TO_RAD);
  const ky = METERS_PER_DEG_LAT;
  let bestApprox = Infinity;
  let bestLat = null;
  let bestLng = null;

  for (const part of geom.parts) {
    if (part.length === 1) {
      const p = part[0];
      const approx = Math.hypot((p.lng - lng0) * kx, (p.lat - lat0) * ky);
      if (approx < bestApprox) {
        bestApprox = approx;
        bestLat = p.lat;
        bestLng = p.lng;
      }
      continue;
    }

    for (let i = 0; i < part.length - 1; i += 1) {
      const a = part[i];
      const b = part[i + 1];
      const ax = (a.lng - lng0) * kx;
      const ay = (a.lat - lat0) * ky;
      const dx = (b.lng - a.lng) * kx;
      const dy = (b.lat - a.lat) * ky;
      const len2 = dx * dx + dy * dy;
      let t = len2 > 0 ? -(ax * dx + ay * dy) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const approx = Math.hypot(ax + t * dx, ay + t * dy);
      if (approx < bestApprox) {
        bestApprox = approx;
        bestLat = a.lat + t * (b.lat - a.lat);
        bestLng = a.lng + t * (b.lng - a.lng);
      }
    }
  }

  return {
    distanceM: bestLat === null ? Infinity : computeDistanceMeters(lat0, lng0, bestLat, bestLng),
    lat: bestLat,
    lng: bestLng,
  };
};
// --- END UPDATE

// --- UPDATE: uniform-grid spatial index — avoids testing every road/pipe against every critical well
const buildGridIndex = (geoms) => {
  const cells = new Map();
  const oversized = [];

  geoms.forEach((g) => {
    const i0 = Math.floor(g.bbox.minLat / GRID_CELL_DEG);
    const i1 = Math.floor(g.bbox.maxLat / GRID_CELL_DEG);
    const j0 = Math.floor(g.bbox.minLng / GRID_CELL_DEG);
    const j1 = Math.floor(g.bbox.maxLng / GRID_CELL_DEG);

    if ((i1 - i0 + 1) * (j1 - j0 + 1) > MAX_GRID_CELLS_PER_ITEM) {
      oversized.push(g);
      return;
    }
    for (let i = i0; i <= i1; i += 1) {
      for (let j = j0; j <= j1; j += 1) {
        const key = `${i}:${j}`;
        const bucket = cells.get(key);
        if (bucket) bucket.push(g);
        else cells.set(key, [g]);
      }
    }
  });

  return { cells, oversized };
};

const searchBoxAround = (lat, lng, radiusM) => {
  const dLat = radiusM / METERS_PER_DEG_LAT;
  const dLng = radiusM / (METERS_PER_DEG_LAT * Math.max(Math.cos(lat * DEG_TO_RAD), 1e-6));
  return { minLat: lat - dLat, maxLat: lat + dLat, minLng: lng - dLng, maxLng: lng + dLng };
};

const bboxIntersects = (a, b) =>
  a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLng <= b.maxLng && a.maxLng >= b.minLng;

const queryGridIndex = (index, box) => {
  const seen = new Set();
  const out = [];
  const i0 = Math.floor(box.minLat / GRID_CELL_DEG);
  const i1 = Math.floor(box.maxLat / GRID_CELL_DEG);
  const j0 = Math.floor(box.minLng / GRID_CELL_DEG);
  const j1 = Math.floor(box.maxLng / GRID_CELL_DEG);

  for (let i = i0; i <= i1; i += 1) {
    for (let j = j0; j <= j1; j += 1) {
      const bucket = index.cells.get(`${i}:${j}`);
      if (!bucket) continue;
      for (const g of bucket) {
        if (!seen.has(g)) {
          seen.add(g);
          out.push(g);
        }
      }
    }
  }
  index.oversized.forEach((g) => {
    if (!seen.has(g)) out.push(g);
  });
  return out;
};

const findNearbyGeometries = (lat, lng, index, radiusM) => {
  const box = searchBoxAround(lat, lng, radiusM);
  const hits = [];
  for (const geom of queryGridIndex(index, box)) {
    if (!bboxIntersects(geom.bbox, box)) continue;
    const nearest = nearestOnGeometry(lat, lng, geom);
    if (nearest.distanceM <= radiusM) hits.push({ geom, ...nearest });
  }
  hits.sort((a, b) => a.distanceM - b.distanceM);
  return hits;
};
// --- END UPDATE

// --- UPDATE: memoised asset & sensor models (keyed on array identity — treat inputs as immutable, as React state is)
const assetModelCache = new WeakMap();
const sensorIndexCache = new WeakMap();

const buildAssetModel = (assets, cfg) => {
  const wellTypes = new Set(cfg.wellTypes.map((t) => String(t).toLowerCase()));
  const roadTypes = new Set(cfg.roadTypes.map((t) => String(t).toLowerCase()));
  const pipeTypes = new Set(cfg.pipeTypes.map((t) => String(t).toLowerCase()));

  const wells = [];
  const roadGeoms = [];
  const pipeGeoms = [];
  let roadsWithoutGeometry = 0;
  let pipesWithoutGeometry = 0;

  for (const asset of assets) {
    if (!asset || typeof asset !== 'object') continue;
    const type = normalizeType(asset);
    if (wellTypes.has(type)) {
      wells.push(asset);
    } else if (roadTypes.has(type)) {
      const geom = buildGeometry(asset);
      if (geom) roadGeoms.push(geom);
      else roadsWithoutGeometry += 1;
    } else if (pipeTypes.has(type)) {
      const geom = buildGeometry(asset);
      if (geom) pipeGeoms.push(geom);
      else pipesWithoutGeometry += 1;
    }
  }

  return {
    wells,
    roadIndex: buildGridIndex(roadGeoms),
    pipeIndex: buildGridIndex(pipeGeoms),
    roadCount: roadGeoms.length,
    pipeCount: pipeGeoms.length,
    roadsWithoutGeometry,
    pipesWithoutGeometry,
  };
};

const getAssetModel = (assets, cfg) => {
  if (!cfg.useCache) return buildAssetModel(assets, cfg);
  const key = `${cfg.wellTypes.join(',')}|${cfg.roadTypes.join(',')}|${cfg.pipeTypes.join(',')}`;
  let byKey = assetModelCache.get(assets);
  if (!byKey) {
    byKey = new Map();
    assetModelCache.set(assets, byKey);
  }
  let model = byKey.get(key);
  if (!model) {
    model = buildAssetModel(assets, cfg);
    byKey.set(key, model);
  }
  return model;
};

const buildSensorIndex = (sensors) => {
  const index = new Map();
  for (const s of sensors) {
    if (!s || typeof s !== 'object') continue;
    const keys = new Set();
    if (s.asset_id !== undefined && s.asset_id !== null) keys.add(String(s.asset_id));
    if (s.linked_entity_id !== undefined && s.linked_entity_id !== null) {
      keys.add(String(s.linked_entity_id));
    }
    keys.forEach((k) => {
      const bucket = index.get(k);
      if (bucket) bucket.push(s);
      else index.set(k, [s]);
    });
  }
  return index;
};

const getSensorIndex = (sensors, useCache) => {
  if (!useCache) return buildSensorIndex(sensors);
  let index = sensorIndexCache.get(sensors);
  if (!index) {
    index = buildSensorIndex(sensors);
    sensorIndexCache.set(sensors, index);
  }
  return index;
};
// --- END UPDATE

// --- UPDATE: reading classification
/**
 * Classifies a raw water-level value (status string or numeric percentage) into a severity.
 * @param {string|number} level
 * @param {Object} [config=FLOOD_RISK_DEFAULTS]
 * @returns {string} One of FLOOD_SEVERITY
 */
export const classifyWaterLevel = (level, config = FLOOD_RISK_DEFAULTS) => {
  const raw = String(level ?? '').toUpperCase().trim();
  const num = parseFloat(level);
  const hasNum = Number.isFinite(num);

  if (config.criticalStatuses.includes(raw) || (hasNum && num >= config.criticalThreshold)) {
    return FLOOD_SEVERITY.CRITICAL;
  }
  if (config.highStatuses.includes(raw) || (hasNum && num >= config.highThreshold)) {
    return FLOOD_SEVERITY.HIGH;
  }
  if (config.warningStatuses.includes(raw) || (hasNum && num >= config.warningThreshold)) {
    return FLOOD_SEVERITY.WARNING;
  }
  return FLOOD_SEVERITY.NONE;
};

const resolveConfig = (hazardRadiusMeters, options) => {
  const fromObject =
    hazardRadiusMeters && typeof hazardRadiusMeters === 'object' ? hazardRadiusMeters : null;
  const merged = { ...FLOOD_RISK_DEFAULTS, ...(fromObject || options || {}) };

  if (!fromObject && hazardRadiusMeters !== undefined && hazardRadiusMeters !== null) {
    merged.hazardRadiusMeters = hazardRadiusMeters;
  }

  const radius = Number(merged.hazardRadiusMeters);
  merged.hazardRadiusMeters =
    Number.isFinite(radius) && radius > 0 ? radius : FLOOD_RISK_DEFAULTS.hazardRadiusMeters;

  const pipeRadius = Number(merged.pipeHazardRadiusMeters);
  merged.pipeHazardRadiusMeters =
    Number.isFinite(pipeRadius) && pipeRadius > 0
      ? pipeRadius
      : FLOOD_RISK_DEFAULTS.pipeHazardRadiusMeters;

  const toUpperList = (list, fallback) =>
    (Array.isArray(list) ? list : fallback).map((v) => String(v).toUpperCase().trim());
  merged.warningStatuses = toUpperList(merged.warningStatuses, FLOOD_RISK_DEFAULTS.warningStatuses);
  merged.highStatuses = toUpperList(merged.highStatuses, FLOOD_RISK_DEFAULTS.highStatuses);
  merged.criticalStatuses = toUpperList(
    merged.criticalStatuses,
    FLOOD_RISK_DEFAULTS.criticalStatuses
  );
  merged.wellTypes = Array.isArray(merged.wellTypes) ? merged.wellTypes : FLOOD_RISK_DEFAULTS.wellTypes;
  merged.roadTypes = Array.isArray(merged.roadTypes) ? merged.roadTypes : FLOOD_RISK_DEFAULTS.roadTypes;
  merged.pipeTypes = Array.isArray(merged.pipeTypes) ? merged.pipeTypes : FLOOD_RISK_DEFAULTS.pipeTypes;

  return merged;
};

const isBetterCandidate = (candidate, best) => {
  if (!best) return true;
  const rankDiff = getSeverityRank(candidate.severity) - getSeverityRank(best.severity);
  if (rankDiff !== 0) return rankDiff > 0;
  if (candidate.isStale !== best.isStale) return !candidate.isStale;
  return (candidate.tsMs ?? -Infinity) > (best.tsMs ?? -Infinity);
};

/** An empty model result — handy as a stable fallback for UIs. */
export const EMPTY_FLOOD_RISK_RESULT = Object.freeze({
  criticalWellIds: new Set(),
  criticalWells: EMPTY_ARRAY,
  roadAlerts: EMPTY_ARRAY,
  highRiskPipes: EMPTY_ARRAY,
  watchWells: EMPTY_ARRAY,
  summary: Object.freeze({
    evaluatedWells: 0,
    criticalWellCount: 0,
    watchWellCount: 0,
    impactedRoadCount: 0,
    highRiskPipeCount: 0,
    highestSeverity: FLOOD_SEVERITY.NONE,
    staleReadingCount: 0,
    evaluatedAt: null,
  }),
  diagnostics: Object.freeze({
    wellsMissingCoordinates: EMPTY_ARRAY,
    roadsWithoutGeometry: 0,
    pipesWithoutGeometry: 0,
    indexedRoads: 0,
    indexedPipes: 0,
    durationMs: 0,
  }),
});
// --- END UPDATE

/**
 * Evaluates assets, sensors, and live telemetry to calculate flood risk zones.
 * @param {Array} assets All infrastructure assets
 * @param {Array} sensors Registered sensor telemetry units
 * @param {Object} waterLevelReadings Real-time water readings map { [deviceId]: { level, timestamp } }
 * @param {number} [hazardRadiusMeters=400] Maximum buffer radius for road inundation
 * @returns {Object} { criticalWellIds, criticalWells, roadAlerts, highRiskPipes }
 */
// --- UPDATE: 4th argument may also be an options object (see FLOOD_RISK_DEFAULTS); optional 5th `options`.
// Additional return keys: watchWells, summary, diagnostics. readings may be a plain object or a Map.
// --- END UPDATE
export const evaluateFloodRiskModel = (
  assets = [],
  sensors = [],
  waterLevelReadings = {},
  hazardRadiusMeters = 400,
  options = {}
) => {
  const startedAt =
    typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  const cfg = resolveConfig(hazardRadiusMeters, options);
  const safeAssets = Array.isArray(assets) ? assets : EMPTY_ARRAY;
  const safeSensors = Array.isArray(sensors) ? sensors : EMPTY_ARRAY;
  const readings = waterLevelReadings || {};
  const now = Number.isFinite(cfg.now) ? cfg.now : Date.now();

  // 🧠 Extract AI/ML weather predictions passed via options
  const aiPredictions = options?.aiPredictions || {};

  const model = getAssetModel(safeAssets, cfg);
  const sensorIndex = getSensorIndex(safeSensors, cfg.useCache);
  const watchWells = [];
  const wellsMissingCoordinates = [];
  let staleReadingCount = 0;

  const criticalWells = [];
  const roadAlerts = [];

  // 1. Filter and assess municipal extraction wells
  model.wells.forEach((well) => {
    const linkedSensors = sensorIndex.get(String(well.id)) || EMPTY_ARRAY;
    const aiInference = aiPredictions[String(well.id)] || null;

    let best = null;

    for (const s of linkedSensors) {
      const dId = s.device_id || s.id;
      const reading = getReading(readings, dId);
      if (!reading) continue;

      let severity = classifyWaterLevel(reading.level, cfg);

      // 🧠 AI Overtopping Override: If ML forecasts overflow in <= 45 mins, escalate to CRITICAL
      if (
        aiInference &&
        aiInference.estimatedMinutesToOvertopping !== null &&
        aiInference.estimatedMinutesToOvertopping !== undefined &&
        aiInference.estimatedMinutesToOvertopping <= 45
      ) {
        severity = FLOOD_SEVERITY.CRITICAL;
      } else if (aiInference && aiInference.floodRiskProbability >= 0.85) {
        severity = FLOOD_SEVERITY.HIGH;
      }

      if (severity === FLOOD_SEVERITY.NONE) continue;

      const tsMs = parseTimestamp(reading.timestamp ?? reading.recorded_at ?? reading.ts);
      const ageMs = tsMs !== null ? Math.max(0, now - tsMs) : null;
      const isStale =
        cfg.maxReadingAgeMs !== null &&
        cfg.maxReadingAgeMs !== undefined &&
        (ageMs === null || ageMs > cfg.maxReadingAgeMs);

      if (isStale) {
        staleReadingCount += 1;
        if (cfg.excludeStaleReadings) continue;
      }

      const candidate = { severity, reading, sensorId: dId, tsMs, ageMs, isStale, aiInference };
      if (isBetterCandidate(candidate, best)) best = candidate;
    }

    if (!best) return;

    const lat = toCoord(well.latitude);
    const lng = toCoord(well.longitude);
    if (!isValidLatLng(lat, lng)) {
      wellsMissingCoordinates.push(String(well.id));
      return;
    }

    const numericLevel = parseFloat(best.reading.level);
    const entry = {
      wellId: String(well.id),
      wellName: well.name || `Well ${well.id}`,
      lat,
      lng,
      reading: best.reading,
      sensorId: best.sensorId,
      severity: best.severity,
      levelValue: Number.isFinite(numericLevel) ? numericLevel : null,
      readingTimestamp: best.tsMs,
      readingAgeMs: best.ageMs,
      isStale: best.isStale,
      linkedSensorCount: linkedSensors.length,
      aiPrediction: best.aiInference, // 👈 Attached ML risk metadata
    };

    if (getSeverityRank(best.severity) >= SEVERITY_RANK.HIGH) criticalWells.push(entry);
    else watchWells.push(entry);
  });

  criticalWells.sort((a, b) => getSeverityRank(b.severity) - getSeverityRank(a.severity));
  // --- END UPDATE

  // 2. Identify vulnerable transportation corridors in proximity to overflowing wells
  // --- UPDATE: spatial-index candidate lookup + true point-to-polyline distance along the whole road
  const impactedRoadIds = new Set();
  const pipeHits = new Map();

  criticalWells.forEach((critWell) => {
    const hits = findNearbyGeometries(
      critWell.lat,
      critWell.lng,
      model.roadIndex,
      cfg.hazardRadiusMeters
    );

    const impactedRoads = hits.slice(0, cfg.maxImpactedRoadsPerWell).map((hit) => {
      const road = hit.geom.asset;
      return {
        roadId: road.id,
        roadName: road.name || `Road ${road.id}`,
        distanceM: Math.round(hit.distanceM),
        lat: hit.lat,
        lng: hit.lng,
        matchedBy: hit.geom.matchedBy,
      };
    });
    hits.forEach((hit) => impactedRoadIds.add(String(hit.geom.asset.id)));

    if (impactedRoads.length > 0) {
      roadAlerts.push({
        wellId: critWell.wellId,
        wellName: critWell.wellName,
        lat: critWell.lat,
        lng: critWell.lng,
        sensorId: critWell.sensorId,
        impactedRoads,
        severity: critWell.severity,
        levelValue: critWell.levelValue,
        reading: critWell.reading,
        readingTimestamp: critWell.readingTimestamp,
        isStale: critWell.isStale,
        totalImpactedRoads: hits.length,
        nearestDistanceM: impactedRoads[0].distanceM,
        aiPrediction: critWell.aiPrediction,
      });
    }

    // 3. Water mains within the pipe buffer of an overflowing well (deduped; closest/most severe well wins)
    findNearbyGeometries(
      critWell.lat,
      critWell.lng,
      model.pipeIndex,
      cfg.pipeHazardRadiusMeters
    ).forEach((hit) => {
      const pipe = hit.geom.asset;
      const pipeId = String(pipe.id);
      const existing = pipeHits.get(pipeId);
      const rankDiff = existing
        ? getSeverityRank(critWell.severity) - getSeverityRank(existing.severity)
        : 1;
      if (!existing || rankDiff > 0 || (rankDiff === 0 && hit.distanceM < existing.distanceM)) {
        pipeHits.set(pipeId, {
          pipeId: pipe.id,
          pipeName: pipe.name || `Pipe ${pipe.id}`,
          assetType: normalizeType(pipe),
          distanceM: Math.round(hit.distanceM),
          lat: hit.lat,
          lng: hit.lng,
          wellId: critWell.wellId,
          wellName: critWell.wellName,
          severity: critWell.severity,
        });
      }
    });
  });

  roadAlerts.sort(
    (a, b) =>
      getSeverityRank(b.severity) - getSeverityRank(a.severity) ||
      a.nearestDistanceM - b.nearestDistanceM
  );

  const highRiskPipes = Array.from(pipeHits.values()).sort(
    (a, b) =>
      getSeverityRank(b.severity) - getSeverityRank(a.severity) || a.distanceM - b.distanceM
  );

  const highestSeverity =
    criticalWells[0]?.severity || watchWells[0]?.severity || FLOOD_SEVERITY.NONE;
  const finishedAt =
    typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  // --- END UPDATE

  return {
    criticalWellIds: new Set(criticalWells.map((w) => String(w.wellId))),
    criticalWells,
    roadAlerts,
    // --- UPDATE
    highRiskPipes,
    watchWells,
    summary: {
      evaluatedWells: model.wells.length,
      criticalWellCount: criticalWells.length,
      watchWellCount: watchWells.length,
      impactedRoadCount: impactedRoadIds.size,
      highRiskPipeCount: highRiskPipes.length,
      highestSeverity,
      staleReadingCount,
      evaluatedAt: now,
    },
    diagnostics: {
      wellsMissingCoordinates,
      roadsWithoutGeometry: model.roadsWithoutGeometry,
      pipesWithoutGeometry: model.pipesWithoutGeometry,
      indexedRoads: model.roadCount,
      indexedPipes: model.pipeCount,
      durationMs: Math.round((finishedAt - startedAt) * 100) / 100,
    },
    // --- END UPDATE
  };
};